import type { DestFill, FillMode, FillQuality } from '../api';
import { consoleModeLabel } from '../fill';

type Choice = { mode: FillMode | null; quality: FillQuality | null };

const MODES: FillMode[] = ['source', 'floor', 'none'];
const QUALITIES: { v: FillQuality; label: string }[] = [
  { v: 'auto', label: 'Auto' },
  { v: '720p', label: '720p' },
  { v: '1080p', label: '1080p' },
  { v: '1440p', label: '1440p' },
];

const SUB: Record<FillMode, string> = {
  source: 'The platform gets the bitrate your encoder sends.',
  floor: 'Pads the video up to what the platform recommends for the quality. Nothing is added when your video is already above it.',
  none: 'Only your video and audio, without padding.',
};

/**
 * "Bitrate for the platform" in a destination's advanced settings
 * (docs/bitrate-fill.md §4, level 3). The highlighted option is what is in
 * force: the destination's own choice, or the default it inherits (marked).
 * Changing it does not restart the output.
 */
export function FillSettings({ platformLabel, effective, value, live, disabled, onChange }: {
  platformLabel: string;
  /** what the server resolved for the saved choice */
  effective: DestFill;
  value: Choice;
  live: boolean;
  disabled?: boolean;
  onChange: (v: Choice) => void;
}) {
  // A default the destination inherits is the server's; the own choice is the form's.
  const defMode = effective.mode_source === 'destination' ? undefined : effective.mode;
  const defQuality = effective.quality_source === 'destination' ? undefined : effective.quality;
  // Back to the default from a saved own choice: the default itself is known only after saving.
  const shownMode: FillMode | undefined = value.mode ?? defMode;
  const shownQuality: FillQuality | undefined = value.quality ?? defQuality;
  const own = value.mode !== null || value.quality !== null;
  const defaultPending = !own && effective.mode_source === 'destination';

  return (
    <div data-testid="dest-fill-settings" data-mode={shownMode ?? ''} data-own={own} className="flex flex-col gap-2.5 rounded-md border border-line p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[13px] font-medium text-text">Bitrate for {platformLabel}</span>
        {own ? (
          <button type="button" data-testid="fill-reset" disabled={disabled} onClick={() => onChange({ mode: null, quality: null })}
            className="min-h-[44px] md:min-h-8 text-xs text-muted underline-offset-4 hover:text-text hover:underline">
            Use the default
          </button>
        ) : (
          <span data-testid="fill-default" className="font-mono text-[10px] tracking-[.06em] text-muted">DEFAULT</span>
        )}
      </div>

      <div role="radiogroup" aria-label={`Bitrate for ${platformLabel}`} className="flex flex-col gap-1.5">
        {MODES.map((m) => {
          const sel = shownMode === m;
          return (
            <button key={m} type="button" role="radio" aria-checked={sel} data-testid={`fill-mode-${m}`} disabled={disabled}
              onClick={() => onChange({ mode: m, quality: value.quality })}
              className={`flex min-h-[44px] flex-col items-start gap-0.5 rounded-md border px-2.5 py-2 text-left ${sel ? 'border-live/50 bg-live/[.12]' : 'border-line-2 hover:border-muted'}`}>
              <span className={`text-[13px] font-medium ${sel ? 'text-live' : 'text-text'}`}>
                {consoleModeLabel(m, platformLabel)}
                {sel && !own && defMode === m && <span className="ml-1.5 font-mono text-[10px] text-muted">default</span>}
              </span>
              <span className="text-[11px] text-muted">{SUB[m]}</span>
            </button>
          );
        })}
      </div>

      {shownMode === 'floor' && (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-muted">Quality</span>
          <div role="radiogroup" aria-label="Quality" className="grid grid-cols-4 gap-1 rounded-md border border-line p-[3px]">
            {QUALITIES.map((q) => {
              const sel = shownQuality === q.v;
              return (
                <button key={q.v} type="button" role="radio" aria-checked={sel} data-testid={`fill-quality-${q.v}`} disabled={disabled}
                  onClick={() => onChange({ mode: value.mode ?? (defMode === 'floor' ? null : 'floor'), quality: q.v })}
                  className={`min-h-[44px] md:min-h-9 rounded-sm border px-1 text-xs ${sel ? 'border-live/50 bg-live/[.12] text-live' : 'border-transparent text-muted hover:text-text-soft'}`}>
                  {q.label}
                </button>
              );
            })}
          </div>
          <span className="text-[11px] text-muted">Auto follows the resolution and frame rate you stream.</span>
        </div>
      )}

      {defaultPending && (
        <p data-testid="fill-default-pending" className="text-[11px] text-text-soft">The default for {platformLabel} applies after you save.</p>
      )}

      <p data-testid="fill-hint" className="text-[11px] text-muted">
        Fill increases outgoing traffic.{live ? ' Changes apply right away, without reconnecting.' : ''}
      </p>
    </div>
  );
}
